import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '@lib/useUrlState';
import { ListOrdered, Plus, RefreshCw } from 'lucide-react';
import CdCSelector from '../components/CdCSelector';

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n);

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('it-IT') : '—';

interface Imputazione {
  id: string;
  data_competenza: string;
  entita_tipo: string;
  centro_costo_id: string | null;
  commessa_id: string | null;
  direzione: string;
  importo: number;
  note: string | null;
}

const MESI = [
  { v: '', l: 'Tutti i mesi' },
  { v: '1', l: 'Gennaio' },
  { v: '2', l: 'Febbraio' },
  { v: '3', l: 'Marzo' },
  { v: '4', l: 'Aprile' },
  { v: '5', l: 'Maggio' },
  { v: '6', l: 'Giugno' },
  { v: '7', l: 'Luglio' },
  { v: '8', l: 'Agosto' },
  { v: '9', l: 'Settembre' },
  { v: '10', l: 'Ottobre' },
  { v: '11', l: 'Novembre' },
  { v: '12', l: 'Dicembre' },
];

const direzioneBadge = (dir: string) => {
  if (dir === 'dare') return 'bg-red-100 text-red-800';
  if (dir === 'avere') return 'bg-green-100 text-green-800';
  return 'bg-gray-100 text-gray-700';
};

const ImputazioniList: React.FC = () => {
  const navigate = useNavigate();
  const [imputazioni, setImputazioni] = useState<Imputazione[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentYear = new Date().getFullYear();
  const [anno, setAnno] = useUrlState('anno', String(currentYear));
  const [mese, setMese] = useUrlState('mese', '');
  // centro di costo: mantiene l'interfaccia string|null di CdCSelector ma
  // persiste nella URL (chiave 'cc').
  const [ccRaw, setCcRaw] = useUrlState('cc', '');
  const centroCostoId = ccRaw || null;
  const setCentroCostoId = (v: string | null) => setCcRaw(v ?? '');
  const [direzione, setDirezione] = useUrlState('direzione', '');

  const caricaImputazioni = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (anno) params.set('anno', anno);
      if (mese) params.set('mese', mese);
      if (centroCostoId) params.set('centro_costo_id', centroCostoId);
      if (direzione) params.set('direzione', direzione);

      const res = await fetch(`/api/v1/centri-costo/imputazioni?${params}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Errore caricamento imputazioni: ${res.status}`);
      const data: Imputazione[] = await res.json();
      setImputazioni(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [anno, mese, centroCostoId, direzione]);

  useEffect(() => {
    caricaImputazioni();
  }, [caricaImputazioni]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ListOrdered className="h-5 w-5 text-gray-400" />
          <h1 className="text-2xl font-semibold">Imputazioni</h1>
        </div>
        <button
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          onClick={() => navigate('/centri-costo/imputazioni/nuova')}
        >
          <Plus className="h-4 w-4" />
          Nuova imputazione
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Anno</label>
          <input
            type="number"
            className="w-24 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={anno}
            onChange={(e) => setAnno(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Mese</label>
          <select
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={mese}
            onChange={(e) => setMese(e.target.value)}
          >
            {MESI.map((m) => (
              <option key={m.v} value={m.v}>{m.l}</option>
            ))}
          </select>
        </div>
        <div className="min-w-48">
          <label className="block text-xs font-medium text-gray-600 mb-1">Centro</label>
          <CdCSelector
            value={centroCostoId}
            onChange={setCentroCostoId}
            placeholder="Tutti i centri"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Direzione</label>
          <select
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={direzione}
            onChange={(e) => setDirezione(e.target.value)}
          >
            <option value="">Tutte</option>
            <option value="dare">Dare</option>
            <option value="avere">Avere</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Caricamento…
        </div>
      ) : imputazioni.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400 gap-3">
          <ListOrdered className="h-10 w-10 opacity-30" />
          <p className="text-sm">Nessuna imputazione trovata per i filtri selezionati.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Data</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Centro</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Commessa</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Dir.</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Importo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {imputazioni.map((imp) => (
                <tr key={imp.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-gray-600">{fmtDate(imp.data_competenza)}</td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                      {imp.entita_tipo}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-500">
                    {imp.centro_costo_id ?? '—'}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-500">
                    {imp.commessa_id ?? '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${direzioneBadge(imp.direzione)}`}>
                      {imp.direzione}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-medium text-gray-800">
                    {fmtCurrency(imp.importo)}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 max-w-xs truncate">
                    {imp.note ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ImputazioniList;
